import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import SignupForm from "../components/SignupForm";
import { Link } from "react-router-dom";

const SignupPage = () => {
  // State for managing rotating user images
  const [activePersonIndex, setActivePersonIndex] = useState(0);
  // State for managing feature animations
  const [activeFeatureIndex, setActiveFeatureIndex] = useState(0);
  
  const people = [
    { id: 1, image: "/assets/HR.jpg", alt: "Happy HR professional" },
    { id: 2, image: "/assets/payroll.jpg", alt: "Satisfied payroll manager" },
    { id: 3, image: "/assets/accountant.jpg", alt: "Content accountant" },
  ];
  
  const features = [
    { 
      id: 1, 
      title: "HR Management",
      description: "Complete employee lifecycle management",
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"></path>
        </svg>
      ),
      position: "top-0 md:left-24" 
    },
    { 
      id: 2, 
      title: "Payroll Processing",
      description: "Automated salary calculations and payments",
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
        </svg>
      ),
      position: "top-28 md:right-0"
    },
    { 
      id: 3, 
      title: "Accounting",
      description: "Financial reporting and analysis",
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"></path>
        </svg>
      ),
      position: "bottom-16 md:left-0"
    },
    { 
      id: 4, 
      title: "Administration",
      description: "Document and resource management",
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2"></path>
        </svg>
      ),
      position: "bottom-0 md:right-24"
    }
  ];
  
  // Improved animation for rotating through people images
  useEffect(() => {
    const interval = setInterval(() => {
      setActivePersonIndex((prevIndex) => (prevIndex + 1) % people.length);
    }, 5000);
    
    return () => clearInterval(interval);
  }, [people.length]);
  
  // Improved animation for rotating through features
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveFeatureIndex((prevIndex) => (prevIndex + 1) % features.length);
    }, 3000);
    
    return () => clearInterval(interval);
  }, [features.length]);

  // Enhanced animation variants
  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
    exit: { opacity: 0, y: -20, transition: { duration: 0.3 } }
  };

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  return (
    <div className="flex flex-col lg:flex-row min-h-[120vh] bg-gray-50">
      {/* Left side with signup form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 sm:p-12">
        <motion.div 
          variants={fadeInUp}
          initial="initial"
          animate="animate"
          className="w-full max-w-md"
        >
          {/* Signup form with primary-800 background border */}
          <motion.div 
            className="p-1 bg-primary-800 rounded-2xl shadow-xl"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <div className="bg-white p-8 rounded-xl">
              <SignupForm />
            </div>
          </motion.div>
          
          <motion.p 
            className="text-center lg:text-left text-gray-500 text-sm mt-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.6 }}
          >
            By registering, you agree to Payrendr's 
            <Link to="/terms-of-service" className="text-primary-600 hover:text-primary-700"> Terms of Service</Link> and 
            <Link to="/privacy-policy" className="text-primary-600 hover:text-primary-700"> Privacy Policy</Link>.
          </motion.p>
        </motion.div>
      </div>
      
      {/* Right side with animations */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-white">
        <motion.div 
          className="relative z-10 flex flex-col justify-center items-center h-full w-full px-8 text-primary-800"
          variants={staggerContainer}
          initial="initial"
          animate="animate"
        >
          <div className="max-w-md mx-auto text-center">
            <motion.h2 
              className="text-3xl md:text-4xl font-bold mb-4 text-primary-800"
              variants={fadeInUp}
            >
              Join Our Platform Today
            </motion.h2>
            <motion.p 
              className="text-lg mb-12 max-w-sm mx-auto text-primary-600"
              variants={fadeInUp}
            >
              Access all the tools you need to streamline your business operations.
            </motion.p>
            
            {/* Animated person images with improved animations */}
            <div className="relative h-72 w-72 mx-auto mb-16">
              <AnimatePresence mode="wait">
                {people.map((person, index) => (
                  activePersonIndex === index && (
                    <motion.div
                      key={person.id}
                      initial={{ opacity: 0, scale: 0.8, rotateY: -15 }}
                      animate={{ opacity: 1, scale: 1, rotateY: 0 }}
                      exit={{ opacity: 0, scale: 0.8, rotateY: 15 }}
                      transition={{ 
                        duration: 0.7, 
                        ease: "easeInOut",
                        scale: { duration: 0.7, ease: [0.34, 1.56, 0.64, 1] }
                      }}
                      className="absolute inset-0"
                    >
                      <div className="relative w-full h-full rounded-full overflow-hidden border-4 border-primary-600/30 shadow-xl">
                        <img 
                          src={person.image} 
                          alt={person.alt}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    </motion.div>
                  )
                ))}
              </AnimatePresence>
              
              {/* Feature animations positioned around the image */}
              <div className="absolute inset-0 -m-12">
                {features.map((feature, index) => (
                  <motion.div
                    key={feature.id}
                    className={`absolute ${feature.position} transition-all duration-500`}
                    initial={{ opacity: 0, scale: 0.6 }}
                    animate={{ 
                      opacity: activeFeatureIndex === index ? 1 : 0.3,
                      scale: activeFeatureIndex === index ? 1 : 0.8,
                      x: activeFeatureIndex === index ? 0 : (index % 2 === 0 ? -5 : 5)
                    }}
                    transition={{ 
                      duration: 0.6, 
                      ease: "easeOut",
                      scale: { duration: 0.6, ease: [0.34, 1.56, 0.64, 1] }
                    }}
                  >
                    <div 
                      className={`p-2 rounded-lg backdrop-blur-md flex items-start space-x-2 ${
                        activeFeatureIndex === index 
                          ? 'bg-primary-100 shadow-lg' 
                          : 'bg-gray-100'
                      }`}
                      style={{ maxWidth: '180px' }}
                    >
                      <div className={`p-1.5 rounded-full ${
                        activeFeatureIndex === index 
                          ? 'bg-primary-600 text-white' 
                          : 'bg-primary-200 text-primary-700'
                      }`}>
                        {feature.icon}
                      </div>
                      <div>
                        <p className="text-xs font-medium text-primary-800">{feature.title}</p>
                        <p className="text-xs text-primary-600 line-clamp-1">{feature.description}</p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
            
            {/* Benefits section */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8, duration: 0.7, ease: "easeOut" }}
              className="bg-primary-50 p-4 rounded-xl border border-primary-200 max-w-md mx-auto shadow-sm"
            >
              <p className="font-medium text-sm mb-3 text-primary-800">Benefits of joining:</p>
              <ul className="text-sm text-primary-700 space-y-2">
                <li className="flex items-start">
                  <svg className="w-4 h-4 text-primary-600 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                  <span>Streamlined HR processes</span>
                </li>
                <li className="flex items-start">
                  <svg className="w-4 h-4 text-primary-600 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                  <span>Automated payroll management</span>
                </li>
                <li className="flex items-start">
                  <svg className="w-4 h-4 text-primary-600 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                  <span>Advanced reporting and analytics</span>
                </li>
              </ul>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default SignupPage;